import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
    IChecklistReportBusinessAreaData,
    IChecklistReportBusinessUnitData,
    IChecklistReportData,
    IChecklistReportSelection,
    IChecklistReportSelectionData,
    IChecklistReportStatus,
} from './checklist-report';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { isDefined } from '@fi-shared/utils/is-defined';
import { ChecklistReportService } from './checklist-report.service';
import { ChecklistReportSelectionComponent } from './view/checklist-report-selection/checklist-report-selection.component';
import { ChecklistReportSelectReportDialogComponent } from './view/checklist-report-select-report-dialog/checklist-report-select-report-dialog.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';

@Component({
    selector: 'fi-checklist-report-view',
    templateUrl: './checklist-report-view.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./checklist-report-view.component.scss'],
    standalone: false,
})
export class ChecklistReportViewComponent implements OnInit {
    @Input() checklist: ChecklistModel;
    @Input() selections: IChecklistReportSelection[];
    @Input() businessUnitsData: IChecklistReportBusinessUnitData[];
    @Input() businessAreasData: IChecklistReportBusinessAreaData[];
    @Input() reportNames: {
        Id: number;
        Name: string;
    }[];

    @ViewChild('checklistReportSelection', { static: false })
    checklistReportSelection: ChecklistReportSelectionComponent;

    reportData: IChecklistReportData;
    selectionData: IChecklistReportSelectionData;

    constructor(
        private detectDevices: DetectDevicesService,
        private fiDialogsService: FiDialogsService,
        private checklistReportService: ChecklistReportService,
        private stateService: FiStateService,
        private loadingService: LoadingService,
    ) {}

    ngOnInit() {
        this.reportNames.sort(comparator((item) => item.Name.toLowerCase()));
    }

    onSelectionChanged(selectionData: IChecklistReportSelectionData) {
        const params = {
            Id: this.checklist.Id,
        };

        this.selectionData = selectionData;
        this.loadingService.trackPromise(
            this.checklistReportService
                .GetCheckListReportDetails(
                    this.checklist.Id,
                    selectionData.StartDate,
                    selectionData.EndDate,
                    selectionData.BusinessUnitIds,
                )
                .then((data: IChecklistReportData) => {
                    this.reportData = data;
                    this.reportData.Statuses.push(this.buildOkStatusData(this.reportData));
                    this.reportData.Statuses.push(this.buildDeviationStatusData(this.reportData));
                    this.stateService.go(FiStateNames.Checklist.Report.View, params, {
                        location: 'replace',
                        inherit: false,
                    });
                })
                .catch(() => {
                    this.reportData = null;
                }),
        );
    }

    openReportsMobile() {
        let triggerBackOnClose = true,
            closed = false;

        const dialog = this.fiDialogsService
            .custom({
                hideHeader: true,
                hideFooter: true,
                contentComponentRef: ChecklistReportSelectReportDialogComponent,
                contentComponentData: this.reportNames,
                config: {
                    panelClass: 'checklist-select-report-dialog',
                    width: '400px',
                    maxWidth: '100%',
                },
            })
            .onYesButtonClick((result: INameIdObj) => {
                closed = true;
                window.history.back();
                this.reportChangeClick(result);
            })
            .onNoButtonClick(() => {
                closed = true;
                if (triggerBackOnClose) {
                    window.history.back();
                }
            });

        history.pushState({ selectReportDialog: true }, document.title);
        ((onpopstate) => {
            window.onpopstate = function () {
                triggerBackOnClose = false;
                if (!closed) {
                    dialog.close();
                }
                window.onpopstate = onpopstate;
            };
        })(window.onpopstate);
    }

    getStatus(): number {
        return this.stateService.params.status;
    }

    getBusinessUnitId(): number {
        return this.stateService.params.unitId;
    }

    isShowCheckpointsPerUnit(): boolean {
        return isDefined(this.getStatus());
    }

    isShowCheckpointsOnUnit(): boolean {
        return isDefined(this.getBusinessUnitId());
    }

    private buildOkStatusData(reportData: IChecklistReportData): IChecklistReportStatus {
        const result = <IChecklistReportStatus>{
            Status: ChecklistModel.Status.OkCheckpoints,
            PeriodsData: [],
            UnitCheckPointDetails: [],
        };

        reportData.Statuses.some((statusData: IChecklistReportStatus) => {
            if (statusData.Status === ChecklistModel.Status.Ok) {
                result.PeriodsData = statusData.PeriodsData;
                result.UnitCheckPointDetails = statusData.UnitCheckPointDetailsForAllCheckLists;
                return true;
            }
        });

        return result;
    }

    private buildDeviationStatusData(reportData: IChecklistReportData): IChecklistReportStatus {
        const result = <IChecklistReportStatus>{
            Status: ChecklistModel.Status.DeviationCheckpoints,
            PeriodsData: [],
            UnitCheckPointDetails: [],
        };

        reportData.Statuses.some((statusData: IChecklistReportStatus) => {
            if (statusData.Status === ChecklistModel.Status.Deviation) {
                result.PeriodsData = statusData.PeriodsData;
                result.UnitCheckPointDetails = statusData.UnitCheckPointDetailsForAllCheckLists;
                return true;
            }
        });

        return result;
    }

    reportChangeClick(checklist: INameIdObj) {
        // replace current state instead of using "ui-sref" directive because we don't need to push it into history
        // by clicking on "back" button user should go to previous stage before he goes to report page
        this.stateService.go(
            this.stateService.current.name,
            { Id: checklist.Id },
            {
                inherit: false,
                location: 'replace',
            },
        );
    }

    // important: chart makes whole application lagging when hidden by css
    isShowBlockCheckMobile(): boolean {
        return !this.detectDevices.isMobilePhone();
    }

    openMobileSelection() {
        this.checklistReportSelection.openMobileSelection();
    }
}
